import { isIndirectFire, MAX_HP } from "@dailywars/core/engine";
import type { Coord, GameEvent, GameState } from "@dailywars/core/engine";

import type { CanvasCombatTracer, CanvasFloatingNumber } from "#/game/animation/types";
import { PLAYER_COLORS } from "#/game/constants";

function attackerTileFromCombatEvents({
  attacked,
  moved,
  beforeState,
  afterState,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  moved: Extract<GameEvent, { type: "moved" }> | undefined;
  beforeState: GameState;
  afterState: GameState;
}): Coord | null {
  if (moved && moved.path.length > 0) {
    return moved.path[moved.path.length - 1]!;
  }
  return (
    afterState.units.find((u) => u.id === attacked.attackerId)?.pos ??
    beforeState.units.find((u) => u.id === attacked.attackerId)?.pos ??
    null
  );
}

function damageNumberForAttack({
  attacked,
  targetTile,
  beforeState,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  targetTile: Coord;
  beforeState: GameState;
}): CanvasFloatingNumber | null {
  const targetUnit = beforeState.units.find((u) => u.id === attacked.targetId);
  if (!targetUnit || attacked.damage <= 0) return null;
  const effectiveDamage = Math.min(attacked.damage, targetUnit.hp);
  const percent = Math.max(1, Math.round((effectiveDamage / MAX_HP) * 100));
  return { tile: targetTile, percent, kind: "damage" };
}

function counterDamageNumber({
  countered,
  attackerTile,
  beforeState,
}: {
  countered: Extract<GameEvent, { type: "countered" }>;
  attackerTile: Coord;
  beforeState: GameState;
}): CanvasFloatingNumber | null {
  if (countered.damage <= 0) return null;
  const counterTarget = beforeState.units.find((u) => u.id === countered.targetId);
  if (!counterTarget) return null;
  const effectiveDamage = Math.min(countered.damage, counterTarget.hp);
  const percent = Math.max(1, Math.round((effectiveDamage / MAX_HP) * 100));
  return { tile: attackerTile, percent, kind: "damage" };
}

function combatTracers({
  attacked,
  countered,
  hasCounterDamage,
  attackerTile,
  targetTile,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  countered: Extract<GameEvent, { type: "countered" }> | undefined;
  hasCounterDamage: boolean;
  attackerTile: Coord;
  targetTile: Coord;
}): CanvasCombatTracer[] {
  const tracers: CanvasCombatTracer[] = [
    { from: attackerTile, to: targetTile, color: PLAYER_COLORS[attacked.playerId] },
  ];
  if (countered && hasCounterDamage) {
    tracers.push({
      from: targetTile,
      to: attackerTile,
      color: PLAYER_COLORS[countered.playerId],
    });
  }
  return tracers;
}

function combatDamageNumbers({
  attacked,
  countered,
  attackerTile,
  targetTile,
  beforeState,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  countered: Extract<GameEvent, { type: "countered" }> | undefined;
  attackerTile: Coord;
  targetTile: Coord;
  beforeState: GameState;
}): { damageNumbers: CanvasFloatingNumber[]; hasCounter: boolean } {
  const damageNumbers: CanvasFloatingNumber[] = [];
  const primary = damageNumberForAttack({ attacked, targetTile, beforeState });
  if (primary) damageNumbers.push(primary);
  const counter = countered ? counterDamageNumber({ countered, attackerTile, beforeState }) : null;
  if (counter) damageNumbers.push(counter);
  return { damageNumbers, hasCounter: counter !== null };
}

function combatTilesFromEvents({
  attacked,
  events,
  beforeState,
  afterState,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  events: GameEvent[];
  beforeState: GameState;
  afterState: GameState;
}): { attackerTile: Coord; targetTile: Coord } | null {
  const moved = events.find(
    (e): e is Extract<GameEvent, { type: "moved" }> =>
      e.type === "moved" && e.unitId === attacked.attackerId,
  );
  const attackerTile = attackerTileFromCombatEvents({ attacked, moved, beforeState, afterState });
  const targetTile =
    beforeState.units.find((u) => u.id === attacked.targetId)?.pos ??
    afterState.units.find((u) => u.id === attacked.targetId)?.pos ??
    null;
  if (!attackerTile || !targetTile) return null;
  return { attackerTile, targetTile };
}

function combatTracersAndDirect({
  attacked,
  countered,
  hasCounter,
  attackerTile,
  targetTile,
  beforeState,
}: {
  attacked: Extract<GameEvent, { type: "attacked" }>;
  countered: Extract<GameEvent, { type: "countered" }> | undefined;
  hasCounter: boolean;
  attackerTile: Coord;
  targetTile: Coord;
  beforeState: GameState;
}): { tracers: CanvasCombatTracer[]; direct: boolean } {
  const attackerKind = beforeState.units.find((u) => u.id === attacked.attackerId)?.kind;
  const direct = attackerKind ? !isIndirectFire(attackerKind) : false;
  const tracers = direct
    ? []
    : combatTracers({
        attacked,
        countered,
        hasCounterDamage: hasCounter,
        attackerTile,
        targetTile,
      });
  return { tracers, direct };
}

export function attackFxInfo({
  beforeState,
  events,
  afterState,
}: {
  beforeState: GameState;
  events: GameEvent[];
  afterState: GameState;
}): {
  attackerTile: Coord;
  targetTile: Coord;
  damageNumbers: CanvasFloatingNumber[];
  tracers: CanvasCombatTracer[];
  hasCounter: boolean;
  direct: boolean;
} | null {
  const attacked = events.find(
    (e): e is Extract<GameEvent, { type: "attacked" }> => e.type === "attacked",
  );
  if (!attacked) return null;

  const tiles = combatTilesFromEvents({ attacked, events, beforeState, afterState });
  if (!tiles) return null;
  const { attackerTile, targetTile } = tiles;

  const countered = events.find(
    (e): e is Extract<GameEvent, { type: "countered" }> => e.type === "countered",
  );
  const { damageNumbers, hasCounter } = combatDamageNumbers({
    attacked,
    countered,
    attackerTile,
    targetTile,
    beforeState,
  });

  const { tracers, direct } = combatTracersAndDirect({
    attacked,
    countered,
    hasCounter,
    attackerTile,
    targetTile,
    beforeState,
  });

  return { attackerTile, targetTile, damageNumbers, tracers, hasCounter, direct };
}
